import { shouldBlankMoneyCellForPrint } from '@/utils/printTableLayout'
import { formatDeliveryOrderPrintQuantity } from '@/utils/formatDeliveryOrderPrintQuantity'

/**
 * 打印表格底部「合计」行：与表头列一一对应，数量列合计数量，金额列合计金额。
 */

/** 与 PrintPreview 无列配置时的默认表头顺序一致（用于合计行对齐） */
export const PRINT_PREVIEW_LEGACY_COLUMNS = [
  { field: 'serial', align: 'center', fontSize: 12 },
  { field: 'order_no', align: 'left', fontSize: 12 },
  { field: 'product_name', align: 'left', fontSize: 12 },
  { field: 'specification', align: 'left', fontSize: 12 },
  { field: 'color', align: 'left', fontSize: 12 },
  { field: 'quantity', align: 'right', fontSize: 12 },
  { field: 'unit', align: 'center', fontSize: 12 },
  { field: 'notes', align: 'left', fontSize: 12 }
]

function formatQty (v, printTemplateType) {
  if (v === null || v === undefined || v === '') return ''
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  if (printTemplateType === 'delivery_order') return formatDeliveryOrderPrintQuantity(n)
  return String(n)
}

function formatMoney (v) {
  if (v === null || v === undefined || v === '') return ''
  const n = Number(v)
  if (Number.isNaN(n)) return ''
  return n.toFixed(2)
}

const AMOUNT_FIELDS = new Set(['line_amount', 'item_amount', 'amount', 'debit', 'credit'])
const NUMERIC_TOTAL = new Set([
  'quantity',
  'pack_quantity',
  'line_amount',
  'item_amount',
  'amount',
  'unit_price',
  'debit',
  'credit',
  'gw_kg',
  'nw_kg',
  'cbm'
])

function columnFieldTrim (col) {
  if (col == null || col.field == null) return ''
  return String(col.field).trim()
}

/**
 * 合计行某一列的显示文本（与表头列下标一一对应）
 * @param {number} [colIndex] 列下标；建议传入，避免多列「字段代码」为空时按 field 查找错位
 * @param {object} [tableConfig] 与模板 tableConfig 一致；开启 printHideMoneyColumns 时金额格不显示数值
 */
export function getTotalRowCellText (col, printData, columns, colIndex, tableConfig, printTemplateType) {
  if (!col || !columns?.length || !printData) return ''
  const f = columnFieldTrim(col)
  if (shouldBlankMoneyCellForPrint(f, tableConfig)) return ''
  const idx =
    typeof colIndex === 'number' && colIndex >= 0 && colIndex < columns.length
      ? colIndex
      : columns.findIndex((c) => columnFieldTrim(c) === f)
  if (idx < 0) return ''

  if (f === 'dye_fee') {
    // 染费不汇总到合计行（与明细「零不显示」一致）
    return ''
  }
  if (f === 'debit') {
    const v = printData.total_debit != null ? printData.total_debit : printData.total_amount
    return formatMoney(v)
  }
  if (f === 'credit') {
    return formatMoney(printData.total_credit)
  }
  if (f === 'balance') return ''

  const serialIdx = columns.findIndex((c) => columnFieldTrim(c) === 'serial')
  if (serialIdx >= 0) {
    if (idx === serialIdx) return '合计'
    if (!f) return ''
    if (f === 'quantity' || f === 'pack_quantity') {
      return formatQty(printData.total_quantity, printTemplateType)
    }
    if (f === 'gw_kg') {
      const v = printData.total_gw_kg
      return v == null || v === '' ? '' : String(v)
    }
    if (f === 'nw_kg') {
      const v = printData.total_nw_kg
      return v == null || v === '' ? '' : String(v)
    }
    if (f === 'cbm') {
      const v = printData.total_cbm
      return v == null || v === '' ? '' : String(v)
    }
    if (AMOUNT_FIELDS.has(f)) {
      return formatMoney(printData.total_amount)
    }
    if (f === 'unit_price') return ''
    return ''
  }

  const first = columns[0]
  const firstF = columnFieldTrim(first)
  const firstIsNumeric = firstF && NUMERIC_TOTAL.has(firstF)
  if (!f) {
    if (idx === 0 && first && !firstIsNumeric) return '合计'
    return ''
  }
  if (idx === 0 && !firstIsNumeric) return '合计'
  if (idx === 0 && firstF === 'quantity') return formatQty(printData.total_quantity, printTemplateType)
  if (f === 'quantity' && idx !== 0) return formatQty(printData.total_quantity, printTemplateType)
  if (AMOUNT_FIELDS.has(f)) {
    return formatMoney(printData.total_amount)
  }
  if (f === 'unit_price') return ''
  return ''
}

export function shouldShowTotalsRow (printData, tableConfig) {
  if (tableConfig?.showTotalsRow === false) return false
  if (!printData?.items?.length) return false
  const cols = tableConfig?.columns
  const hasAmt =
    printData.total_amount != null ||
    printData.total_debit != null ||
    printData.total_credit != null
  const hasQty = printData.total_quantity != null && printData.total_quantity !== ''
  if (!cols?.length) {
    return hasQty || hasAmt
  }
  const fields = cols.map((c) => columnFieldTrim(c)).filter(Boolean)
  const wantsQty = fields.includes('quantity')
  const wantsAmt = fields.some((x) => AMOUNT_FIELDS.has(x) || x === 'balance')
  if (!wantsQty && !wantsAmt) return false
  if (wantsAmt && hasAmt) return true
  if (wantsQty && hasQty) return true
  if (wantsQty && !hasQty && wantsAmt && hasAmt) return true
  return false
}
